"use client"

import { useEffect, useState } from "react"
import type { RefObject } from "react"

/**
 * The <video> element the modal is currently showing, as state, so listener effects can re-attach when it actually
 * appears. Effects that only read `videoRef.current` ran once with `null` when the modal opened straight onto a video
 * (the dialog's content mounts a moment after the first commit) and never ran again, so the video <-> script sync, the
 * pause ad and the gutter dimming were silently missing on the first video opened (they only worked after moving to
 * another post). Checks once per frame until the element exists; cheap, and it stops as soon as it finds one.
 */
export function useMountedVideo(videoRef: RefObject<HTMLVideoElement | null>, isMediaVideo: boolean, videoKey: string | null): HTMLVideoElement | null {
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null)
  useEffect(() => {
    if (!isMediaVideo) {
      setVideoEl(null)
      return
    }
    let raf = 0
    const check = () => {
      const el = videoRef.current
      if (el) setVideoEl(el)
      else raf = requestAnimationFrame(check)
    }
    check()
    return () => cancelAnimationFrame(raf)
  }, [videoRef, isMediaVideo, videoKey])
  return videoEl
}
